import type { StateCreator } from 'zustand';
import type {
  Lead,
  SaleCall,
  SalesKpis,
  TimingInsights,
  CorrelationsResult,
  ScriptVersion,
  ScriptVariant,
  VariantLeaderboardEntry,
  EvolutionSuggestion,
  ScriptSection,
  LibraryScript,
  EffectiveScript,
  ObjectionCategory,
  CallObjection,
  NicheSettings,
  FitScore,
  JobPosting,
} from '../types';
import { salesCrmApi } from '../services/salesCrmApi';
import type { DialKontext, SalesCrmPrefill } from '../services/leadGenApi';

// 'popout' = aus dem Tab gezogen, eigenes Betriebssystem-Fenster (lib/fensterAuslagern.tsx).
// Es ist ein MODUS und kein zweiter Anruf: dasselbe Bauteil, derselbe Timer, dieselbe Skript-
// Position — nur haengt das DOM in einem anderen Fenster.
export type CallWindowMode = 'window' | 'full' | 'min' | 'popout';

// Fenster oder Vollbild ist eine Gewohnheit, keine Entscheidung je Anruf — sie überlebt deshalb den
// Neustart. 'min' wird bewusst NICHT gemerkt: „zusammengeklappt" beschreibt einen laufenden Anruf,
// kein Arbeitsverhalten; gemerkt ginge der nächste Call unsichtbar auf.
const CALL_WINDOW_MODE_KEY = 'call_window_mode';

/** Darf dieser Modus als Gewohnheit ueberleben?
 *
 * Zwei Modi duerfen es NICHT, und beide aus einem Grund, der beim naechsten Laden zuschlaegt:
 *  - 'min' beschreibt einen laufenden Anruf, kein Arbeitsverhalten — gemerkt ginge der naechste
 *    Call unsichtbar auf.
 *  - 'popout' braucht einen Klick, um ein Fenster zu oeffnen. Beim Laden gibt es den nicht, der
 *    Browser verweigert das Fenster — und der Anruf liefe in einem Cockpit, das nirgends steht.
 *
 * Eigene Funktion statt einer Bedingung im Setter, weil genau das die Art Regel ist, die beim
 * naechsten hinzugefuegten Modus vergessen wird: so steht sie an EINER Stelle und hat einen Test.
 */
export function modusDarfBleiben(m: CallWindowMode): boolean {
  return m !== 'min' && m !== 'popout';
}

function ladeCallWindowMode(): CallWindowMode {
  try {
    return localStorage.getItem(CALL_WINDOW_MODE_KEY) === 'full' ? 'full' : 'window';
  } catch {
    return 'window';   // privater Modus / gesperrter Speicher: Fenster ist der Standard
  }
}

export interface SalesCrmSlice {
  leads: Lead[];
  // Fraud0 Telefon → Sales CRM handoff: prefill for the "add lead" form (consumed by ListbuildingTab).
  salesCrmPendingPrefill: SalesCrmPrefill | null;
  setSalesCrmPendingPrefill: (p: SalesCrmPrefill | null) => void;
  calls: SaleCall[];
  salesScript: string;
  scriptVersion: number;
  salesKpis: SalesKpis | null;
  timingInsights: TimingInsights | null;
  correlations: CorrelationsResult | null;
  salesLoading: boolean;
  activeLead: Lead | null;
  activeLeadCalls: SaleCall[];
  callViewOpen: boolean;
  // Wie das Cockpit dasteht. 'window' = Pop-up neben der übrigen App (Standard seit 2026-08-11,
  // Nutzerentscheidung), 'full' = das frühere Vollbild, 'min' = zusammengeklappt auf die kleine
  // „Call läuft"-Marke. `callViewOpen` bleibt in ALLEN dreien wahr — auch zusammengeklappt läuft der
  // Anruf weiter, das Bauteil bleibt hängen und Timer, Notizen und Skript-Position bleiben stehen.
  callWindowMode: CallWindowMode;
  // Welche Nummer welcher Person diesen Anruf ausgelöst hat. Nur gesetzt, wenn über den einen
  // Wählweg der App gewählt wurde (`useTrackedDial`) — wer das Cockpit aus der Lead-Liste heraus
  // öffnet, hat keinen Anschluss geklickt, und dann ist `null` die richtige Auskunft. Das Ergebnis
  // „Nummer falsch" bucht darauf; ohne den Zusammenhang bliebe nur Raten.
  aktuelleWahl: DialKontext | null;
  callTimerStart: number | null;
  scriptVersions: ScriptVersion[];

  // Darwin Evolution state
  scriptVariants: ScriptVariant[];
  activeVariant: ScriptVariant | null;
  personalizedScript: { sections_data: Record<string, ScriptSection>; section_order: string[]; hidden_sections?: string[] } | null;
  variantLeaderboard: VariantLeaderboardEntry[];
  evolutionSuggestion: EvolutionSuggestion | null;
  salesNiches: string[];

  // Autopilot state
  nicheSettings: Record<string, NicheSettings>;

  // Objection Tracking state
  objectionCategories: ObjectionCategory[];
  flaggedObjections: CallObjection[];

  // Skript-Bibliothek state
  libraryScripts: LibraryScript[];
  libraryLoading: boolean;

  fetchLeads: (status?: string, niche?: string) => Promise<void>;
  fetchCalls: (leadId?: number) => Promise<void>;
  fetchScript: () => Promise<void>;
  fetchKpis: () => Promise<void>;
  fetchTimingInsights: (niche?: string) => Promise<void>;
  fetchCorrelations: (niche?: string, minSample?: number) => Promise<void>;
  addLead: (data: Partial<Lead>) => Promise<Lead>;
  updateLead: (id: number, data: Partial<Lead>) => Promise<void>;
  deleteLead: (id: number) => Promise<void>;
  addCall: (data: {
    lead_id: number;
    length_seconds?: number;
    outcome?: string;
    emotions?: string[];
    notes?: string;
    caller_id?: string;
    call_time?: string;
    callback_at?: string;   // UTC-ISO mit 'Z' — wann wieder anrufen
    script_variant_id?: number;
    call_script_sections?: Record<string, { title: string; body: string }>;
    call_script_section_order?: string[];
    personalization_context?: Record<string, unknown>;
  }) => Promise<string | null>;
  startCall: (lead: Lead, variantId?: number, wahl?: DialKontext | null) => void;
  setCallWindowMode: (mode: CallWindowMode) => void;
  endCall: () => void;
  saveScript: (content: string) => Promise<void>;
  fetchScriptVersions: () => Promise<void>;
  restoreScriptVersion: (id: number) => Promise<void>;

  // Darwin Evolution actions
  fetchVariants: (niche?: string, status?: string) => Promise<void>;
  createVariant: (data: Partial<ScriptVariant>) => Promise<ScriptVariant>;
  updateVariant: (id: number, data: Partial<ScriptVariant>) => Promise<void>;
  activateVariant: (id: number) => Promise<void>;
  archiveVariant: (id: number) => Promise<void>;
  selectVariant: (variant: ScriptVariant | null) => void;
  evolveVariants: (ids: number[], niche: string) => Promise<ScriptVariant>;
  seedVariant: (sourceId: number, niche: string, context?: string) => Promise<ScriptVariant>;
  personalizeScript: (variantId: number, leadId: number) => Promise<void>;
  clearPersonalizedScript: () => void;
  fetchLeaderboard: (niche?: string) => Promise<void>;
  fetchEvolutionSuggestion: (niche?: string) => Promise<void>;
  fetchSalesNiches: (lang?: string) => Promise<void>;
  saveSectionTags: (callId: number, strongest: string | null, weakest: string | null) => Promise<void>;

  // Skript-Bibliothek
  fetchLibraryScripts: (niche?: string, language?: string) => Promise<void>;
  addLibraryScript: (data: {
    name: string;
    content?: string;
    sections_data?: Record<string, ScriptSection>;
    section_order?: string[];
    niche?: string;
    language?: string;
    notes?: string;
  }) => Promise<LibraryScript>;
  updateLibraryScript: (id: number, data: Partial<LibraryScript> & { content?: string }) => Promise<LibraryScript>;
  removeLibraryScript: (id: number) => Promise<void>;
  adoptLibraryScript: (id: number) => Promise<{ fork: ScriptVariant; main: ScriptVariant | null; script: EffectiveScript }>;
  setLibraryScriptAsMain: (id: number) => Promise<void>;

  // Autopilot actions
  setNicheSettings: (niche: string, settings: NicheSettings) => void;

  // Objection Tracking actions
  setObjectionCategories: (categories: ObjectionCategory[]) => void;
  setFlaggedObjections: (objections: CallObjection[]) => void;

  // Job Discovery actions
  leadsFromJobs: (data: { search_terms: string[]; location: string; max_results: number; auto_score: boolean }) => Promise<{ job_id: string; status: string; message: string }>;
  scoreLead: (leadId: number) => Promise<FitScore[]>;
  leadToOutreach: (leadId: number) => Promise<{ target_id: number; mapped_fields: Record<string, unknown> }>;
  getLeadJobPostings: (leadId: number) => Promise<JobPosting[]>;
}

export const createSalesCrmSlice: StateCreator<SalesCrmSlice> = (set, get) => ({
  leads: [],
  salesCrmPendingPrefill: null,
  setSalesCrmPendingPrefill: (p) => set({ salesCrmPendingPrefill: p }),
  calls: [],
  salesScript: '',
  scriptVersion: 0,
  salesKpis: null,
  timingInsights: null,
  correlations: null,
  salesLoading: false,
  activeLead: null,
  activeLeadCalls: [],
  callViewOpen: false,
  callWindowMode: ladeCallWindowMode(),
  aktuelleWahl: null,
  callTimerStart: null,
  scriptVersions: [],

  // Darwin Evolution initial state
  scriptVariants: [],
  activeVariant: null,
  personalizedScript: null,
  variantLeaderboard: [],
  evolutionSuggestion: null,
  salesNiches: [],

  // Autopilot initial state
  nicheSettings: {},

  // Objection Tracking initial state
  objectionCategories: [],
  flaggedObjections: [],

  // Skript-Bibliothek initial state
  libraryScripts: [],
  libraryLoading: false,

  fetchLeads: async (status, niche) => {
    set({ salesLoading: true });
    try {
      const leads = await salesCrmApi.listLeads(status, niche);
      set({ leads, salesLoading: false });
    } catch {
      set({ salesLoading: false });
    }
  },

  fetchCalls: async (leadId) => {
    try {
      const calls = await salesCrmApi.listCalls(leadId ? { lead_id: leadId } : undefined);
      set({ calls });
    } catch {}
  },

  fetchScript: async () => {
    try {
      const result = await salesCrmApi.getScript();
      set({ salesScript: result.content, scriptVersion: result.version });
    } catch {}
  },

  fetchKpis: async () => {
    try {
      const salesKpis = await salesCrmApi.getKpis();
      set({ salesKpis });
    } catch {}
  },

  fetchTimingInsights: async (niche) => {
    try {
      const timingInsights = await salesCrmApi.getTimingInsights(niche);
      set({ timingInsights });
    } catch {}
  },

  fetchCorrelations: async (niche, minSample) => {
    try {
      const correlations = await salesCrmApi.getCorrelations(niche, minSample);
      set({ correlations });
    } catch {}
  },

  addLead: async (data) => {
    const lead = await salesCrmApi.addLead(data);
    set((s) => ({ leads: [lead, ...s.leads] }));
    return lead;
  },

  updateLead: async (id, data) => {
    const updated = await salesCrmApi.updateLead(id, data);
    set((s) => ({ leads: s.leads.map((l) => (l.id === id ? updated : l)) }));
  },

  deleteLead: async (id) => {
    // Soft-Delete: status='deleted' wird serverseitig aus der Default-Liste gefiltert
    await salesCrmApi.updateLead(id, { status: 'deleted' as Lead['status'] });
    set((s) => ({ leads: s.leads.filter((l) => l.id !== id) }));
  },

  addCall: async (data) => {
    const result = await salesCrmApi.addCall(data);
    await get().fetchCalls();
    await get().fetchLeads();
    return result.suggested_status;
  },

  startCall: (lead, variantId, wahl) => {
    const state = get();
    let activeVariant = state.activeVariant;
    if (variantId !== undefined) {
      // Fork-IDs (Git-Schicht) liegen NICHT in scriptVariants · in dem Fall die zuvor per selectVariant
      // gesetzte Effektiv-Variante behalten, statt sie auf null zu clobbern (sonst zeigt der Call das
      // Legacy-Markdown statt des effektiven Skripts).
      activeVariant = state.scriptVariants.find((v) => v.id === variantId) ?? state.activeVariant;
    }
    // Ein neuer Anruf geht IMMER sichtbar auf. Stünde der Modus noch auf 'min' (vom vorigen Call),
    // klingelte es beim Gegenüber, während hier nur eine Marke am Rand aufleuchtet.
    const mode = state.callWindowMode === 'min' ? ladeCallWindowMode() : state.callWindowMode;
    set({ activeLead: lead, callViewOpen: true, callTimerStart: Date.now(), activeVariant,
          callWindowMode: mode, aktuelleWahl: wahl ?? null });
  },

  setCallWindowMode: (mode) => {
    // Nur die Gewohnheit merken, nicht den Zwischenstand — siehe ladeCallWindowMode.
    if (modusDarfBleiben(mode)) {
      try { localStorage.setItem(CALL_WINDOW_MODE_KEY, mode); } catch { /* Speicher gesperrt */ }
    }
    set({ callWindowMode: mode });
  },

  endCall: () => {
    set({
      activeLead: null,
      callViewOpen: false,
      callTimerStart: null,
      activeVariant: null,
      personalizedScript: null,
      // Mit muss er weg: bliebe der Zusammenhang stehen, bekäme der NÄCHSTE Anruf — der vielleicht
      // aus der Lead-Liste ohne Nummernklick startet — das Ergebnis „Nummer falsch" auf den
      // Anschluss des vorigen gebucht.
      aktuelleWahl: null,
    });
  },

  saveScript: async (content) => {
    await salesCrmApi.saveScript(content);
    set({ salesScript: content });
    await get().fetchScript();
  },

  fetchScriptVersions: async () => {
    try {
      const scriptVersions = await salesCrmApi.getScriptVersions();
      set({ scriptVersions });
    } catch {}
  },

  restoreScriptVersion: async (id) => {
    const version = await salesCrmApi.getScriptVersion(id);
    set({ salesScript: version.content, scriptVersion: version.version });
  },

  // ---------------------------------------------------------------------------
  // Darwin Evolution actions
  // ---------------------------------------------------------------------------

  fetchVariants: async (niche, status) => {
    try {
      const scriptVariants = await salesCrmApi.listVariants({ niche, status });
      set({ scriptVariants });
    } catch {}
  },

  createVariant: async (data) => {
    const variant = await salesCrmApi.createVariant(data);
    set((s) => ({ scriptVariants: [variant, ...s.scriptVariants] }));
    return variant;
  },

  updateVariant: async (id, data) => {
    const updated = await salesCrmApi.updateVariant(id, data);
    set((s) => ({
      scriptVariants: s.scriptVariants.map((v) => (v.id === id ? updated : v)),
    }));
  },

  activateVariant: async (id) => {
    await salesCrmApi.activateVariant(id);
    set((s) => ({
      scriptVariants: s.scriptVariants.map((v) =>
        v.id === id ? { ...v, status: 'active' as const } : v
      ),
    }));
  },

  archiveVariant: async (id) => {
    await salesCrmApi.archiveVariant(id);
    set((s) => ({
      scriptVariants: s.scriptVariants.map((v) =>
        v.id === id ? { ...v, status: 'archived' as const } : v
      ),
    }));
  },

  selectVariant: (variant) => {
    set({ activeVariant: variant, personalizedScript: null });
  },

  evolveVariants: async (ids, niche) => {
    const variant = await salesCrmApi.evolveVariants(ids, niche);
    set((s) => ({ scriptVariants: [variant, ...s.scriptVariants] }));
    return variant;
  },

  seedVariant: async (sourceId, niche, context) => {
    const variant = await salesCrmApi.seedVariant(sourceId, niche, context);
    set((s) => ({ scriptVariants: [variant, ...s.scriptVariants] }));
    return variant;
  },

  personalizeScript: async (variantId, leadId) => {
    const result = await salesCrmApi.personalizeVariant(variantId, leadId);
    set({ personalizedScript: result });
  },

  clearPersonalizedScript: () => {
    set({ personalizedScript: null });
  },

  fetchLeaderboard: async (niche) => {
    try {
      const variantLeaderboard = await salesCrmApi.getVariantLeaderboard(niche);
      set({ variantLeaderboard });
    } catch {}
  },

  fetchEvolutionSuggestion: async (niche) => {
    try {
      const evolutionSuggestion = await salesCrmApi.getEvolutionSuggestion(niche);
      set({ evolutionSuggestion });
    } catch {}
  },

  fetchSalesNiches: async (lang?: string) => {
    try {
      const salesNiches = await salesCrmApi.getNiches(lang);
      set({ salesNiches });
    } catch {}
  },

  saveSectionTags: async (callId, strongest, weakest) => {
    await salesCrmApi.saveSectionTags(callId, strongest, weakest);
  },

  // ---------------------------------------------------------------------------
  // Skript-Bibliothek
  // ---------------------------------------------------------------------------

  fetchLibraryScripts: async (niche, language) => {
    set({ libraryLoading: true });
    try {
      const libraryScripts = await salesCrmApi.listLibraryScripts({ niche, language });
      set({ libraryScripts });
    } catch {
    } finally {
      set({ libraryLoading: false });
    }
  },

  addLibraryScript: async (data) => {
    const entry = await salesCrmApi.addLibraryScript(data);
    set((s) => ({ libraryScripts: [entry, ...s.libraryScripts] }));
    return entry;
  },

  updateLibraryScript: async (id, data) => {
    const updated = await salesCrmApi.updateLibraryScript(id, data);
    set((s) => ({ libraryScripts: s.libraryScripts.map((e) => (e.id === id ? { ...e, ...updated } : e)) }));
    return updated;
  },

  removeLibraryScript: async (id) => {
    await salesCrmApi.removeLibraryScript(id);
    set((s) => ({ libraryScripts: s.libraryScripts.filter((e) => e.id !== id) }));
  },

  adoptLibraryScript: async (id) => {
    const res = await salesCrmApi.adoptLibraryScript(id);
    // Genau ein Skript ist aktiv → alte Markierung fällt weg.
    set((s) => ({
      libraryScripts: s.libraryScripts.map((e) => ({
        ...e,
        adopted_by_me: e.id === id,
        adopted_count: e.id === id && !e.adopted_by_me ? e.adopted_count + 1 : e.adopted_count,
      })),
    }));
    return res;
  },

  setLibraryScriptAsMain: async (id) => {
    await salesCrmApi.setLibraryScriptAsMain(id);
  },

  setNicheSettings: (niche, settings) => {
    set((s) => ({ nicheSettings: { ...s.nicheSettings, [niche]: settings } }));
  },

  setObjectionCategories: (categories) => {
    set({ objectionCategories: categories });
  },

  setFlaggedObjections: (objections) => {
    set({ flaggedObjections: objections });
  },

  // Job Discovery
  leadsFromJobs: async (data) => {
    return salesCrmApi.leadsFromJobs(data);
  },

  scoreLead: async (leadId) => {
    const result = await salesCrmApi.scoreLead(leadId);
    // Refresh the lead list so fit scores show up
    const leads = get().leads.map((l) =>
      l.id === leadId ? { ...l, fit_scores: result.scores } : l
    );
    set({ leads });
    return result.scores;
  },

  leadToOutreach: async (leadId) => {
    return salesCrmApi.leadToOutreach(leadId);
  },

  getLeadJobPostings: async (leadId) => {
    return salesCrmApi.getLeadJobPostings(leadId);
  },
});
